import type {
  ContactCompany,
  ContactCompanyActivity,
  ContactCompanyMember,
  ContactCompanyOwnerId,
  ContactCompanyRole,
} from '@otw/models/leads/contactCompanies'
import {
  normalizeContactCompanyDropoffLocationIds,
  normalizeContactCompanyTerminalIds,
} from '@otw/models/leads/contactCompanies'
import type { Contact } from '@otw/models/leads/contacts'
import type { Lead } from '@otw/models/leads/lead'

export type MergeFieldSource = 'primary' | 'secondary'

export type MergeContactCompaniesFieldKey =
  | 'name'
  | 'website'
  | 'notes'
  | 'ownerId'
  | 'businessAddressId'
  | 'dropoffLocationIds'

export type MergeContactCompaniesFieldSources = Record<
  MergeContactCompaniesFieldKey,
  MergeFieldSource
>

export interface MergeContactCompaniesInput {
  primaryCompanyId: string
  secondaryCompanyId: string
  fieldSources: MergeContactCompaniesFieldSources
}

const ROLE_RANK: Record<ContactCompanyRole, number> = {
  owner: 0,
  manager: 1,
  partner: 2,
}

const pickString = (
  primary: string | undefined,
  secondary: string | undefined,
  source: MergeFieldSource,
): string | undefined => {
  const primaryVal = primary?.trim() ?? ''
  const secondaryVal = secondary?.trim() ?? ''
  const chosen = source === 'primary' ? primaryVal : secondaryVal
  const fallback = source === 'primary' ? secondaryVal : primaryVal
  return chosen || fallback || undefined
}

const pickOwnerId = (
  primary: ContactCompanyOwnerId,
  secondary: ContactCompanyOwnerId,
  source: MergeFieldSource,
): ContactCompanyOwnerId => {
  const primarySet =
    primary !== null && primary !== undefined && String(primary).trim() !== ''
  const secondarySet =
    secondary !== null && secondary !== undefined && String(secondary).trim() !== ''
  if (source === 'primary') {
    return primarySet ? primary : secondarySet ? secondary : null
  }
  return secondarySet ? secondary : primarySet ? primary : null
}

const pickLocationId = (
  primary: string | null | undefined,
  secondary: string | null | undefined,
  source: MergeFieldSource,
): string | null | undefined => {
  const primaryVal = primary?.trim() ?? ''
  const secondaryVal = secondary?.trim() ?? ''
  const chosen = source === 'primary' ? primaryVal : secondaryVal
  const fallback = source === 'primary' ? secondaryVal : primaryVal
  return chosen || fallback || undefined
}

export const mergeCompanyMembers = (
  primaryMembers: ContactCompanyMember[],
  secondaryMembers: ContactCompanyMember[],
): ContactCompanyMember[] => {
  const byContactId = new Map<string, ContactCompanyMember>()

  for (const member of [...primaryMembers, ...secondaryMembers]) {
    const existing = byContactId.get(member.contactId)
    if (!existing) {
      byContactId.set(member.contactId, member)
      continue
    }
    const keepCurrent =
      ROLE_RANK[member.role] < ROLE_RANK[existing.role] ||
      (ROLE_RANK[member.role] === ROLE_RANK[existing.role] &&
        member.addedAt.getTime() < existing.addedAt.getTime())
    byContactId.set(
      member.contactId,
      keepCurrent
        ? {
            ...member,
            addedAt:
              member.addedAt.getTime() < existing.addedAt.getTime()
                ? member.addedAt
                : existing.addedAt,
          }
        : existing,
    )
  }

  return Array.from(byContactId.values())
}

export const mergeCompanyActivities = (
  primaryActivities: ContactCompanyActivity[],
  secondaryActivities: ContactCompanyActivity[],
): ContactCompanyActivity[] =>
  [...primaryActivities, ...secondaryActivities].sort(
    (a, b) => b.createdAt.getTime() - a.createdAt.getTime(),
  )

const maxActivityDate = (
  primary: Date | null,
  secondary: Date | null,
): Date | null => {
  const primaryMs = primary?.getTime() ?? -Infinity
  const secondaryMs = secondary?.getTime() ?? -Infinity
  const maxMs = Math.max(primaryMs, secondaryMs)
  return maxMs === -Infinity ? null : new Date(maxMs)
}

export const buildMergedContactCompany = (
  primary: ContactCompany,
  secondary: ContactCompany,
  fieldSources: MergeContactCompaniesFieldSources,
): ContactCompany => {
  const now = new Date()
  const mergedName =
    pickString(primary.name, secondary.name, fieldSources.name)?.trim() ??
    primary.name.trim()

  const merged: ContactCompany = {
    ...primary,
    name: mergedName,
    ownerId: pickOwnerId(primary.ownerId, secondary.ownerId, fieldSources.ownerId),
    members: mergeCompanyMembers(primary.members, secondary.members),
    activities: mergeCompanyActivities(primary.activities ?? [], secondary.activities ?? []),
    lastActivityAt: maxActivityDate(primary.lastActivityAt, secondary.lastActivityAt),
    updatedAt: now,
  }

  const website = pickString(primary.website, secondary.website, fieldSources.website)
  if (website) merged.website = website
  else delete merged.website

  const notes = pickString(primary.notes, secondary.notes, fieldSources.notes)
  if (notes) merged.notes = notes
  else delete merged.notes

  const businessAddressId = pickLocationId(
    primary.businessAddressId,
    secondary.businessAddressId,
    fieldSources.businessAddressId,
  )
  if (businessAddressId) merged.businessAddressId = businessAddressId
  else delete merged.businessAddressId

  const chosenDropoffs =
    fieldSources.dropoffLocationIds === 'primary'
      ? primary.dropoffLocationIds
      : secondary.dropoffLocationIds
  const fallbackDropoffs =
    fieldSources.dropoffLocationIds === 'primary'
      ? secondary.dropoffLocationIds
      : primary.dropoffLocationIds
  merged.dropoffLocationIds = normalizeContactCompanyDropoffLocationIds(
    chosenDropoffs.length > 0 ? chosenDropoffs : fallbackDropoffs,
  )
  delete (merged as ContactCompany & { dropoffLocationId?: unknown }).dropoffLocationId

  // Always union terminal associations so neither company's links are dropped.
  merged.terminalIds = normalizeContactCompanyTerminalIds([
    ...(primary.terminalIds ?? []),
    ...(secondary.terminalIds ?? []),
  ])

  return merged
}

const namesMatch = (a: string, b: string): boolean =>
  a.trim().toLowerCase() === b.trim().toLowerCase()

/** @deprecated Contacts no longer store company name strings; membership lives on companies. */
export const applyMergedCompanyNameToContact = (
  _contact: Contact,
  _secondaryName: string,
  _mergedName: string,
): Contact | null => null

/** Update lead company field when it matched the merged-away company name. */
export const applyMergedCompanyNameToLead = (
  lead: Lead,
  secondaryName: string,
  mergedName: string,
): Lead | null => {
  if (!namesMatch(lead.company, secondaryName)) return null
  return { ...lead, company: mergedName }
}

export const getDefaultMergeFieldSources = (
  primary: ContactCompany,
  secondary: ContactCompany,
): MergeContactCompaniesFieldSources => {
  const preferSource = (
    primaryVal: string | undefined | null,
    secondaryVal: string | undefined | null,
  ): MergeFieldSource => {
    const p = typeof primaryVal === 'string' ? primaryVal.trim() : ''
    const s = typeof secondaryVal === 'string' ? secondaryVal.trim() : ''
    if (p && !s) return 'primary'
    if (s && !p) return 'secondary'
    return 'primary'
  }

  return {
    name: preferSource(primary.name, secondary.name),
    website: preferSource(primary.website, secondary.website),
    notes: preferSource(primary.notes, secondary.notes),
    ownerId: preferSource(
      primary.ownerId != null ? String(primary.ownerId) : '',
      secondary.ownerId != null ? String(secondary.ownerId) : '',
    ),
    businessAddressId: preferSource(primary.businessAddressId, secondary.businessAddressId),
    dropoffLocationIds: preferSource(
      primary.dropoffLocationIds.length > 0 ? 'set' : '',
      secondary.dropoffLocationIds.length > 0 ? 'set' : '',
    ),
  }
}
